/**
 * Instellingen: bovenaan Profiel en Weergave voor iedereen (Instellingen.tsx), daaronder
 * het beheer (hoofdstuk 5 en eis 2.6), alleen voor wie beheer mag zien.
 *
 * Een lijst met coderingcategorieën, een inline bewerkbare tabel per categorie, de
 * rollen- en rechtenmatrix en het wijzigingslog. De categorieën
 * hieronder zijn één op één de tabellen uit het coderingenbestand; elke categorie
 * weet zelf hoe hij uit het coderingendocument gelezen en er weer in geschreven
 * wordt, zodat de tabel-UI generiek kan blijven.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Coderingen, StapSoort } from '@houtstaat/engine';

import { api, type AuditRegel, type Ik } from '../api';
import { bouwnummerKort, datumTijd, useGegevens } from '../gegevens';
import { Cel } from '../onderdelen/Cel';
import { Schakelaar } from '../onderdelen/Dialoog';
import { IconPlus } from '../onderdelen/Iconen';
import { Maatlijn } from '../onderdelen/Maatlijn';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { actieLabel, Wijzigingslog } from '../onderdelen/Wijzigingslog';
import { RECHTLABEL, ROLUITLEG, type RolSleutel } from '../rechten';
import { Profiel, Weergave } from './Instellingen';

type Rij = string[];

interface Categorie {
  sleutel: string;
  label: string;
  groep: 'coderingen' | 'algemeen' | 'toegang';
  kolommen: (c: Coderingen) => string[];
  lees: (c: Coderingen) => Rij[];
  schrijf?: (c: Coderingen, rijen: Rij[]) => Coderingen;
  toelichting?: string;
}

const getal = (v: string): number | null => {
  const s = v.trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const tekst = (v: unknown): string => (v == null ? '' : String(v));

/** Een lijst met één kolom (bv. "Kaal materiaal"). */
const enkeleKolom = (
  sleutel: string,
  label: string,
  kop: string,
  veld: keyof Coderingen,
  toelichting?: string,
): Categorie => ({
  sleutel,
  label,
  groep: 'coderingen',
  kolommen: () => [kop],
  lees: (c) => (c[veld] as string[]).map((v) => [v]),
  schrijf: (c, rijen) => ({ ...c, [veld]: rijen.map((r) => tekst(r[0])).filter(Boolean) }),
  ...(toelichting ? { toelichting } : {}),
});

const CATEGORIEEN: Categorie[] = [
  {
    sleutel: 'kanten',
    label: 'Kantenband (Kanten)',
    groep: 'coderingen',
    kolommen: (c) => ['Materiaal', ...c.kanten.kanttypes],
    lees: (c) =>
      c.kanten.regels.map((r) => [r.materiaal, ...c.kanten.kanttypes.map((k) => tekst(r.toeslagen[k]))]),
    schrijf: (c, rijen) => ({
      ...c,
      kanten: {
        ...c.kanten,
        regels: rijen.map((r) => ({
          materiaal: tekst(r[0]),
          toeslagen: Object.fromEntries(
            c.kanten.kanttypes
              .map((k, i) => [k, getal(tekst(r[i + 1]))] as const)
              .filter(([, v]) => v != null) as Array<[string, number]>,
          ),
        })),
      },
    }),
    toelichting:
      'Toeslag per kantenbandtype en materiaal. Een negatieve waarde betekent dat het paneel kleiner ' +
      'gezaagd wordt omdat de band er nog bij komt.',
  },
  {
    sleutel: 'fineer',
    label: 'Fineer diktes',
    groep: 'coderingen',
    kolommen: () => ['Materiaal', 'Dikte (mm)', 'Max. baanbreedte (mm)'],
    lees: (c) => c.fineerDiktes.map((f) => [f.materiaal, tekst(f.dikte), tekst(f.breedte)]),
    schrijf: (c, rijen) => ({
      ...c,
      fineerDiktes: rijen.map((r) => ({
        materiaal: tekst(r[0]),
        dikte: getal(tekst(r[1])),
        breedte: getal(tekst(r[2])),
      })),
    }),
    toelichting:
      'Deze tabel bepaalt twee dingen: of een onderdeel de fineerroute krijgt, en de calibreerdikte ' +
      '(netto dikte min de fineerdikte).',
  },
  {
    sleutel: 'hpl',
    label: 'HPL-materialen',
    groep: 'coderingen',
    kolommen: () => ['Code', 'Dikte (mm)', 'Type'],
    lees: (c) => c.hplMaterialen.map((h) => [h.code, tekst(h.dikte), h.type]),
    schrijf: (c, rijen) => ({
      ...c,
      hplMaterialen: rijen.map((r) => ({
        code: tekst(r[0]),
        dikte: getal(tekst(r[1])),
        type: tekst(r[2]) || 'los blad',
      })),
    }),
  },
  {
    sleutel: 'lakdikte',
    label: 'Lakdiktecodes',
    groep: 'coderingen',
    kolommen: () => ['Afwerkingscode', 'Lakdiktecode'],
    lees: (c) => c.lakdikteCodes.map((l) => [l.afwerkingscode, l.lakdiktecode]),
    schrijf: (c, rijen) => ({
      ...c,
      lakdikteCodes: rijen.map((r) => ({ afwerkingscode: tekst(r[0]), lakdiktecode: tekst(r[1]) })),
    }),
  },
  enkeleKolom('overige', 'Overige afwerkingen', 'Afwerking', 'overigeAfwerkingen'),
  enkeleKolom(
    'kaal',
    'Kaal materiaal',
    'Materiaal',
    'kaalMateriaal',
    'Plaatmateriaal zonder afwerking. Onderdeel van de plaatmateriaallijst.',
  ),
  {
    sleutel: 'materiaalHpl',
    label: 'Materiaal HPL (drager)',
    groep: 'coderingen',
    kolommen: () => ['Materiaal', 'Min dikte (mm)', 'Max dikte (mm)'],
    lees: (c) => c.materiaalHpl.map((m) => [m.materiaal, tekst(m.minDikte), tekst(m.maxDikte)]),
    schrijf: (c, rijen) => ({
      ...c,
      materiaalHpl: rijen.map((r) => ({
        materiaal: tekst(r[0]),
        minDikte: getal(tekst(r[1])),
        maxDikte: getal(tekst(r[2])),
      })),
    }),
  },
  {
    sleutel: 'dubbel',
    label: 'Dubbel fineren',
    groep: 'coderingen',
    kolommen: () => ['Materiaal', 'Tegenmateriaal', 'Automatisch (Y)'],
    lees: (c) => c.dubbelFineren.map((d) => [d.materiaal, d.tegenmateriaal, d.automatisch ? 'Y' : '']),
    schrijf: (c, rijen) => ({
      ...c,
      dubbelFineren: rijen.map((r) => ({
        materiaal: tekst(r[0]),
        tegenmateriaal: tekst(r[1]),
        automatisch: tekst(r[2]).trim().toUpperCase() === 'Y',
      })),
    }),
  },
  enkeleKolom('eenzijdig', 'Eenzijdig plakken', 'Materiaal', 'eenzijdigPlakken'),
  enkeleKolom(
    'vasteDikte',
    'Materiaal vaste dikte',
    'Materiaal',
    'materiaalVasteDikte',
    'Deze materialen mogen niet gecalibreerd of opgedikt worden.',
  ),
  {
    sleutel: 'cali',
    label: 'Cali (kalibreren)',
    groep: 'coderingen',
    kolommen: () => ['Materiaal', 'Cali-dikte (mm)'],
    lees: (c) => c.cali.map((x) => [x.materiaal, tekst(x.dikte)]),
    schrijf: (c, rijen) => ({
      ...c,
      cali: rijen.map((r) => ({ materiaal: tekst(r[0]), dikte: getal(tekst(r[1])) })),
    }),
  },
  {
    sleutel: 'speciaal',
    label: 'Speciale bewerkingen',
    groep: 'coderingen',
    kolommen: () => ['Materiaal', 'Bewerking'],
    lees: (c) => c.specialeBewerkingen.map((s) => [s.materiaal, s.bewerking]),
    schrijf: (c, rijen) => ({
      ...c,
      specialeBewerkingen: rijen.map((r) => ({ materiaal: tekst(r[0]), bewerking: tekst(r[1]) })),
    }),
  },
];

const ALGEMEEN = [
  { sleutel: 'machines', label: 'Machinegegevens' },
  { sleutel: 'conversie', label: 'Conversietabel (TSW → DKB)' },
  { sleutel: 'plaatmateriaal', label: 'Plaatmateriaallijst' },
] as const;

export function Beheer() {
  const { boot, ik, mag, meldFout, ververs } = useGegevens();
  const [actief, setActief] = useState<string>('profiel');
  const [coderingen, setCoderingen] = useState<Coderingen | null>(null);
  const [rijen, setRijen] = useState<Rij[]>([]);
  const [vuil, setVuil] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditRegel[]>([]);
  const [algemeen, setAlgemeen] = useState<Record<string, Record<string, unknown>[]> | null>(null);
  const [dagcapaciteit, setDagcapaciteit] = useState<Record<string, number> | null>(null);

  const magBewerken = mag('coderingenBewerken');
  const categorie = CATEGORIEEN.find((c) => c.sleutel === actief) ?? null;

  const magBeheer = mag('beheerBekijken');

  const laad = useCallback(async () => {
    if (!boot || !magBeheer) return;
    try {
      const [c, lijsten, log] = await Promise.all([
        api.coderingen(boot.bouwnummer),
        api.plaatmateriaal(),
        api.audit(boot.bouwnummer),
      ]);
      setCoderingen(c);
      setAlgemeen(lijsten as unknown as Record<string, Record<string, unknown>[]>);
      setDagcapaciteit(lijsten.dagcapaciteit);
      setAudit(log);
      setVuil(false);
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De coderingen konden niet geladen worden.');
    }
  }, [boot, magBeheer, meldFout]);

  useEffect(() => {
    void laad();
  }, [laad]);

  useEffect(() => {
    if (coderingen && categorie) setRijen(categorie.lees(coderingen));
  }, [coderingen, categorie]);

  const opslaan = async () => {
    if (!boot || !coderingen || !categorie?.schrijf) return;
    setBezig(true);
    try {
      const nieuw = categorie.schrijf(coderingen, rijen);
      await api.coderingenOpslaan(boot.bouwnummer, nieuw);
      setCoderingen(nieuw);
      setVuil(false);
      setMelding(`${categorie.label} opgeslagen — de houtstaat is opnieuw doorgerekend.`);
      window.setTimeout(() => setMelding(null), 6000);
      await ververs();
      setAudit(await api.audit(boot.bouwnummer));
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Opslaan mislukt.');
    } finally {
      setBezig(false);
    }
  };

  const algemeneTabel = useMemo(() => {
    const lijst = algemeen?.[actief];
    if (!Array.isArray(lijst) || lijst.length === 0) return null;
    const kolommen = Object.keys(lijst[0] ?? {});
    return {
      kolommen,
      rijen: lijst.slice(0, 400).map((r) => kolommen.map((k) => tekst(r[k]))),
      totaal: lijst.length,
    };
  }, [algemeen, actief]);

  const aantal = (sleutel: string): number => {
    const cat = CATEGORIEEN.find((c) => c.sleutel === sleutel);
    if (cat) return coderingen ? cat.lees(coderingen).length : 0;
    const lijst = algemeen?.[sleutel];
    return Array.isArray(lijst) ? lijst.length : 0;
  };

  const titel =
    categorie?.label ??
    ALGEMEEN.find((a) => a.sleutel === actief)?.label ??
    {
      profiel: 'Profiel',
      weergave: 'Weergave',
      rollen: 'Rollen & rechten',
      instellingen: 'Instellingen boot',
      capaciteit: 'Dagcapaciteit per afdeling',
      audit: 'Wijzigingslog',
    }[actief] ??
    '';

  if (!boot) return <div className="hs-leeg">Kies eerst een boot.</div>;

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div>
            <p className="hs-annotatie hs-kop__gebied">Instellingen</p>
            <h1 className="hs-h1">{titel}</h1>
            <Maatlijn />
            <p className="hs-subregel">
              {actief === 'profiel'
                ? 'Wie je bent in deze applicatie en wat je rol je laat doen.'
                : actief === 'weergave'
                  ? 'Hoe de applicatie op dit apparaat opent en beweegt.'
                  : null}
              {magBeheer && actief !== 'profiel' && actief !== 'weergave' ? (
                <>
                  {categorie ? `${rijen.length} regels · ` : ''}
                  {audit[0]
                    ? `laatst gewijzigd door ${audit[0].gebruiker} · ${datumTijd(audit[0].tijdstip)} · ${actieLabel(
                        audit[0].actie,
                      ).toLowerCase()}`
                    : 'nog geen wijzigingen vastgelegd'}
                  {categorie
                    ? magBewerken
                      ? ' · klik een cel om te bewerken'
                      : ' · je hebt alleen leesrechten'
                    : ''}
                </>
              ) : null}
            </p>
            {/* Coderingen, instellingen en het wijzigingslog horen bij één bouwnummer. */}
            {magBeheer && (categorie || actief === 'instellingen' || actief === 'audit') ? <Bouwnummerfilter /> : null}
          </div>
          <div className="hs-kopacties">
            {melding ? <span className="hs-chip hs-chip--goed">{melding}</span> : null}
            {categorie && magBewerken ? (
              <>
                <button
                  type="button"
                  className="hs-knop"
                  onClick={() => {
                    if (!coderingen) return;
                    setRijen((r) => [...r, categorie.kolommen(coderingen).map(() => '')]);
                    setVuil(true);
                  }}
                >
                  <IconPlus /> Nieuwe regel
                </button>
                <button
                  type="button"
                  className="hs-knop hs-knop--primair"
                  disabled={!vuil || bezig}
                  onClick={() => void opslaan()}
                >
                  {bezig ? 'Opslaan…' : 'Opslaan'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <div className="hs-scherm" style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
        <nav
          className="hs-paneel"
          style={{ width: 260, flex: 'none', position: 'sticky', top: 12, padding: '6px 0 10px' }}
        >
          <Groep label="Persoonlijk" />
          <Keuze label="Profiel" aan={actief === 'profiel'} onKies={() => setActief('profiel')} />
          <Keuze label="Weergave" aan={actief === 'weergave'} onKies={() => setActief('weergave')} />
          {magBeheer ? (
          <>
          <Groep label={`Coderingen boot ${boot.bouwnummer}`} />
          {CATEGORIEEN.map((c) => (
            <Keuze
              key={c.sleutel}
              label={c.label}
              telling={aantal(c.sleutel)}
              aan={actief === c.sleutel}
              onKies={() => setActief(c.sleutel)}
            />
          ))}
          <Groep label="Algemeen (alle boten)" />
          {ALGEMEEN.map((c) => (
            <Keuze
              key={c.sleutel}
              label={c.label}
              telling={aantal(c.sleutel)}
              aan={actief === c.sleutel}
              onKies={() => setActief(c.sleutel)}
            />
          ))}
          <Groep label="Toegang en instellingen" />
          <Keuze
            label="Dagcapaciteit per afdeling"
            aan={actief === 'capaciteit'}
            onKies={() => setActief('capaciteit')}
          />
          <Keuze label="Rollen en rechten" aan={actief === 'rollen'} onKies={() => setActief('rollen')} />
          <Keuze
            label="Instellingen boot"
            aan={actief === 'instellingen'}
            onKies={() => setActief('instellingen')}
          />
          <Keuze
            label="Wijzigingslog"
            telling={audit.length}
            aan={actief === 'audit'}
            onKies={() => setActief('audit')}
          />
          </>
          ) : null}
        </nav>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {categorie?.toelichting ? (
            <p className="hs-muted" style={{ maxWidth: 780 }}>
              {categorie.toelichting}
            </p>
          ) : null}

          {categorie && coderingen ? (
            <section className="hs-paneel" style={{ overflowX: 'auto' }}>
              <table className="hs-tabel">
                <thead>
                  <tr>
                    {categorie.kolommen(coderingen).map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                    {magBewerken ? <th style={{ width: 34 }} /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rijen.map((rij, ri) => (
                    <tr key={ri}>
                      {categorie.kolommen(coderingen).map((_, ci) => (
                        <td key={ci} className={ci === 0 ? 'hs-mono' : undefined}>
                          <Cel
                            waarde={tekst(rij[ci])}
                            magAanpassen={magBewerken}
                            onOpslaan={(v) => {
                              setRijen((huidig) =>
                                huidig.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)),
                              );
                              setVuil(true);
                            }}
                          />
                        </td>
                      ))}
                      {magBewerken ? (
                        <td>
                          <button
                            type="button"
                            className="hs-knop hs-knop--mini"
                            title="Regel verwijderen"
                            onClick={() => {
                              setRijen((huidig) => huidig.filter((_, i) => i !== ri));
                              setVuil(true);
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {algemeneTabel ? (
            <>
              <p className="hs-muted">
                Deze lijst geldt voor álle boten.
                {algemeneTabel.totaal > algemeneTabel.rijen.length
                  ? ` Eerste ${algemeneTabel.rijen.length} van ${algemeneTabel.totaal} regels getoond.`
                  : ''}
              </p>
              <section className="hs-paneel" style={{ maxHeight: '62vh', overflow: 'auto' }}>
                <table className="hs-tabel">
                  <thead>
                    <tr>
                      {algemeneTabel.kolommen.map((k) => (
                        <th key={k}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {algemeneTabel.rijen.map((rij, ri) => (
                      <tr key={ri}>
                        {rij.map((v, ci) => (
                          <td key={ci} className="hs-mono">
                            {v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          ) : null}

          {actief === 'capaciteit' ? (
            <Dagcapaciteit
              waarden={dagcapaciteit}
              onOpgeslagen={(nieuw) => setDagcapaciteit(nieuw)}
            />
          ) : null}
          {actief === 'profiel' ? <Profiel /> : null}
          {actief === 'weergave' ? <Weergave /> : null}
          {actief === 'rollen' ? <Rollenmatrix matrix={ik?.matrix ?? null} /> : null}
          {actief === 'instellingen' ? <Instellingen /> : null}
          {actief === 'audit' ? (
            <section className="hs-paneel" style={{ overflowX: 'auto' }}>
              <Wijzigingslog
                regels={audit}
                leegtekst="Nog geen wijzigingen vastgelegd voor dit bouwnummer."
              />
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Groep({ label }: { label: string }) {
  return (
    <div className="hs-sectielabel" style={{ padding: '9px 12px 4px' }}>
      {label}
    </div>
  );
}

function Keuze({
  label,
  telling,
  aan,
  onKies,
}: {
  label: string;
  telling?: number;
  aan: boolean;
  onKies: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onKies}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 12px',
        border: 0,
        background: aan ? 'var(--chip-zacht-bg)' : 'transparent',
        color: aan ? 'var(--primair)' : 'var(--body-secundair)',
        fontWeight: aan ? 600 : 400,
        fontSize: 'var(--t-body)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {telling != null ? (
        <span className="hs-mono hs-muted-licht" style={{ fontSize: 10.5 }}>
          {telling}
        </span>
      ) : null}
    </button>
  );
}

/**
 * De rollen- en rechtenmatrix, precies zoals de API hem afdwingt.
 *
 * De matrix komt uit `GET /api/me`; er staat geen tweede kopie in de frontend. De
 * labels van de rechten staan in `rechten.ts`, want die zijn puur weergave.
 */
function Rollenmatrix({ matrix }: { matrix: Ik['matrix'] | null }) {
  if (!matrix) return <p className="hs-muted">De rechtenmatrix wordt geladen…</p>;
  return (
    <>
      <p className="hs-muted" style={{ maxWidth: 820 }}>
        De rollen worden toegekend in Entra ID; de Static Web App geeft ze door aan de applicatie. Deze
        matrix komt rechtstreeks uit de API en wordt daar ook afgedwongen — de knoppen in de schermen
        volgen dezelfde rechten, maar zijn niet de grens.
      </p>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {matrix.rollen.map((rol) => (
          <div key={rol.sleutel} className="hs-paneel hs-paneel__inhoud">
            <div className="hs-sectiekop">{rol.label}</div>
            <p className="hs-muted" style={{ margin: '3px 0 7px' }}>
              {ROLUITLEG[rol.sleutel as RolSleutel] ?? ''}
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {matrix.rechten
                .filter((recht) => recht.rollen.includes(rol.sleutel))
                .map((recht) => (
                  <li key={recht.sleutel} style={{ fontSize: 11.5, color: 'var(--body-secundair)' }}>
                    ✓ {RECHTLABEL[recht.sleutel] ?? recht.sleutel}
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Instellingen per boot: de kleur en bij welke bewerkingen een foto verplicht is.
 *
 * De fotoplicht staat per stap op de kar; dit is de standaard die nieuwe karren
 * meekrijgen. Op een bestaande kar is hij per stap aan of uit te zetten via het
 * stap-menu.
 */
function Instellingen() {
  const { boot, mag, meldFout, ververs } = useGegevens();
  const [bezig, setBezig] = useState(false);
  if (!boot) return null;
  const magBewerken = mag('coderingenBewerken');

  const wissel = async (soort: StapSoort, aan: boolean) => {
    setBezig(true);
    try {
      const nieuw = aan
        ? [...boot.fotoVerplichtBij, soort]
        : boot.fotoVerplichtBij.filter((s) => s !== soort);
      await api.bouwnummerBijwerken(boot.bouwnummer, { fotoVerplichtBij: nieuw });
      await ververs();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De instelling kon niet opgeslagen worden.');
    } finally {
      setBezig(false);
    }
  };

  return (
    <section className="hs-paneel hs-paneel__inhoud" style={{ maxWidth: 640 }}>
      <div className="hs-sectiekop">Bouwnummer {bouwnummerKort(boot.bouwnummer)}</div>
      <p className="hs-muted" style={{ margin: '3px 0 10px' }}>
        {boot.omschrijving} · v{boot.versie} · rev {boot.revisie}
      </p>

      <span className="hs-sectielabel">Foto verplicht bij</span>
      <p className="hs-muted" style={{ margin: '3px 0 6px' }}>
        Dit is de standaard voor nieuwe karren. Per kar is het alsnog aan of uit te zetten via het
        stap-menu op de werkvloer.
      </p>
      {FOTOSTAPPEN.map((soort) => (
        <Schakelaar
          key={soort}
          label={soort}
          aan={boot.fotoVerplichtBij.includes(soort)}
          uitgeschakeld={!magBewerken || bezig}
          onWissel={(aan) => void wissel(soort, aan)}
        />
      ))}
    </section>
  );
}

/**
 * De dagcapaciteit per afdeling: de beschikbare productie-uren per werkdag waarmee
 * het dashboard de doorlooptijd en de bezettingsgraad rekent.
 *
 * Dit is een **aanname** tot productievoorbereiding hem heeft vastgesteld (openstaand
 * punt 1 van de werkopdracht), en daarom hier aanpasbaar in plaats van vastgelegd in
 * code: een nieuwe afspraak vraagt geen uitrol. Alleen de admin mag hem wijzigen —
 * hetzelfde recht als voor de normtijden, want het is dezelfde soort urenafspraak.
 */
function Dagcapaciteit({
  waarden,
  onOpgeslagen,
}: {
  waarden: Record<string, number> | null;
  onOpgeslagen: (nieuw: Record<string, number>) => void;
}) {
  const { kleuren, mag, meldFout } = useGegevens();
  const [concept, setConcept] = useState<Record<string, string>>({});
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);
  const magBewerken = mag('normtijdenBewerken');

  const afdelingen = kleuren?.afdelingen ?? [];
  const huidig = (afdeling: string): string =>
    concept[afdeling] ?? String(waarden?.[afdeling] ?? 0).replace('.', ',');
  const vuil = Object.keys(concept).length > 0;

  const opslaan = async () => {
    if (!waarden) return;
    const nieuw: Record<string, number> = { ...waarden };
    for (const [afdeling, tekstwaarde] of Object.entries(concept)) {
      const getal = Number(tekstwaarde.replace(',', '.'));
      if (!Number.isFinite(getal) || getal < 0 || getal > 24) {
        meldFout(`De dagcapaciteit van ${afdeling} moet een getal tussen 0 en 24 uur zijn.`);
        return;
      }
      nieuw[afdeling] = getal;
    }
    setBezig(true);
    try {
      const antwoord = await api.algemeenOpslaan({ dagcapaciteit: nieuw });
      onOpgeslagen(antwoord.dagcapaciteit);
      setConcept({});
      setMelding('Opgeslagen — dashboard en planning rekenen hier vanaf nu mee.');
      window.setTimeout(() => setMelding(null), 6000);
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Opslaan mislukt.');
    } finally {
      setBezig(false);
    }
  };

  if (!waarden) return <p className="hs-muted">De capaciteit wordt geladen…</p>;

  return (
    <section className="hs-paneel hs-paneel__inhoud" style={{ maxWidth: 560 }}>
      <div className="hs-sectiekop">Beschikbare productie-uren per werkdag</div>
      <p className="hs-muted" style={{ margin: '3px 0 12px' }}>
        Het dashboard deelt het resterende werk door deze uren voor de doorlooptijd en de
        bezettingsgraad. <strong>Nog vast te stellen met productievoorbereiding</strong> — de
        waarden hieronder zijn een eerste aanname. Gereed staat op 0: een kar die gereed is
        belast geen afdeling meer.
      </p>

      {afdelingen.map((a) => (
        <label
          key={a.afdeling}
          style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}
        >
          <span className="hs-kleurvak" style={{ background: a.kleur, width: 12, height: 12 }} />
          <span style={{ flex: 1, fontSize: 12.5 }}>{a.afdeling}</span>
          <input
            className="hs-mono hs-invoer"
            value={huidig(a.afdeling)}
            disabled={!magBewerken || bezig}
            onChange={(e) => setConcept((c) => ({ ...c, [a.afdeling]: e.target.value }))}
            style={{ width: 70, textAlign: 'right' }}
            inputMode="decimal"
            aria-label={`Dagcapaciteit ${a.afdeling} in uren`}
          />
          <span className="hs-muted" style={{ width: 46 }}>
            u/dag
          </span>
        </label>
      ))}

      {magBewerken ? (
        <div className="hs-knoprij" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="hs-knop hs-knop--primair"
            disabled={!vuil || bezig}
            onClick={() => void opslaan()}
          >
            {bezig ? 'Bezig…' : 'Opslaan'}
          </button>
          {vuil ? (
            <button type="button" className="hs-knop" onClick={() => setConcept({})}>
              Wijzigingen ongedaan maken
            </button>
          ) : null}
        </div>
      ) : (
        <p className="hs-muted" style={{ marginTop: 10 }}>
          Alleen een admin mag deze uren wijzigen.
        </p>
      )}
      {melding ? (
        <p style={{ marginTop: 10 }}>
          <span className="hs-chip hs-chip--goed">{melding}</span>
        </p>
      ) : null}
    </section>
  );
}

/** De bewerkingen waar een foto zinvol is; persen is de aanleiding geweest. */
const FOTOSTAPPEN: StapSoort[] = ['PERS', 'FINEER', 'SCHUURMACHINE', 'CNCZAAG', 'CONTROLE'];

